import Vue from 'vue';
import store from '@/store';
import pixAxios from '@/axios/pixAxios.js';

import Console from '@/shared/console';

import genericTableService from '@/services/genericConfig/genericTable.utils.js';
import documentUtils from '@/services/document/document.utils.js';

import tableIdConst from '@/consts/tableId.const.js';

const profilesModule = {
  namespaced: true,

  state: {
    selectedProfile: null,
    selectedRole: null,
    profilesTableConfig: [],
    profilesTableData: null,
    rolesTableConfig: [],
    profilesList: [],
  },

  actions: {
    async fetchProfilesTableData({ commit }, { SearchTerm = null, ...tablePaginationConfig }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const { data } = await pixAxios.post('/api/Users/SearchProfiles', { SearchTerm, IncludeProfiles: true, ...tablePaginationConfig });
        commit('setProfilesTableData', data);
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },
    async fetchProfilesTableConfig({ commit }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const config = await genericTableService.getGenericTableCall(tableIdConst.PROFILES);
        commit('setProfilesTableConfig', config);
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async fetchRolesTableConfig({ commit }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const config = await genericTableService.getGenericTableCall(tableIdConst.PROFILES_ASSOCIATED_ROLES);
        commit('setRolesTableConfig', config);
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async fetchProfilesList({ commit }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const { data } = await pixAxios.get('/api/Users/GetListProfilesCombo');
        const mappedData = Object.keys(data).map((key) => ({ ValueCode: data[key], ValueDesc: key }));
        commit('setProfilesList', mappedData);
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async fetchProfilesTableExcel(_, { SearchTerm = null }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        const document = await pixAxios.get('/api/Users/GetListProfilesExcel', {
          SearchTerm,
        });
        documentUtils.exportDocument(document, 'profiles');
      } catch (err) {
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async registerProfile(_, { newProfileInfo: { Profile, ProfileDesc } }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        await pixAxios.post('/api/Users/AddProfile', {
          ProfileRowId: '00000000-0000-0000-0000-000000000000',
          Profile,
          ProfileDesc,
          RoleList: [],
          NumUsers: 0,
          AudTime: new Date(),
          AudUsu: store.getters['authModule/UserName'],
        });
        Vue.toasted.global.pixSuccess('Profile created');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async updateProfile(_, { updatedProfileInfo }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        await pixAxios.put('/api/Users/UpdateProfile', updatedProfileInfo);
        Vue.toasted.global.pixSuccess('Profile updated');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async deleteProfile(_, { selectedProfile: { ProfileRowId } }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        await pixAxios.delete(`/api/Users/DeleteProfile?req=${ProfileRowId}`);
        Vue.toasted.global.pixSuccess('Profile deleted');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async addRole(_, { selectedProfile: { ProfileRowId }, selectedRole: RoleRowId }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        await pixAxios.post('/api/Users/AddProfileRole', {
          ProfileRowId,
          RoleRowId,
        });
        Vue.toasted.global.pixSuccess('Role added');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    async deleteRole(_, { selectedProfile: { ProfileRowId }, selectedRole: { RoleRowId } }) {
      store.dispatch('loadingOverlayModule/setIsLoading', true);
      try {
        await pixAxios.put('/api/Users/DeleteProfileRole', {
          ProfileRowId,
          RoleRowId,
        });
        Vue.toasted.global.pixSuccess('Role deleted');
      } catch (err) {
        Vue.toasted.global.pixError(err);
        Console.log(err);
      } finally {
        store.dispatch('loadingOverlayModule/setIsLoading', false);
      }
    },

    clearState({ commit }) {
      commit('setSelectedProfile', null);
      commit('setSelectedRole', null);
      commit('setProfilesTableConfig', []);
      commit('setProfilesTableData', null);
      commit('setRolesTableConfig', []);
      commit('setProfilesList', []);
    },

    selectProfile({ commit }, { selectedProfile }) {
      commit('setSelectedProfile', selectedProfile);
    },

    selectRole({ commit }, { selectedRole }) {
      commit('setSelectedRole', selectedRole);
    },

    clearStore({ commit }) {
      commit('setSelectedProfile', null);
      commit('setSelectedRole', null);
      commit('setProfilesTableConfig', []);
      commit('setProfilesTableData', null);
      commit('setRolesTableConfig', []);
      commit('setProfilesList', []);
    },
  },

  mutations: {
    setProfilesList(state, payload) {
      Vue.set(state, 'profilesList', payload);
    },
    setProfilesTableData(state, payload) {
      Vue.set(state, 'profilesTableData', payload);
    },
    setProfilesTableConfig(state, payload) {
      Vue.set(state, 'profilesTableConfig', payload);
    },
    setRolesTableConfig(state, payload) {
      Vue.set(state, 'rolesTableConfig', payload);
    },
    setSelectedProfile(state, payload) {
      Vue.set(state, 'selectedProfile', payload);
    },
    setSelectedRole(state, payload) {
      Vue.set(state, 'selectedRole', payload);
    },
  },

  getters: {
    profilesTableData(state) {
      return state.profilesTableData?.rows.map((row) => ({ ...row, NumRoles: row.RoleList.length }));
    },
    selectedProfileAssociatedRoles(state) {
      return state.selectedProfile?.RoleList?.sort((a, b) => (a.Role > b.Role ? 1 : b.Role > a.Role ? -1 : 0)) ?? [];
    },
    profilesTablePagination(state) {
      return state.profilesTableData;
    },
  },
};

export default profilesModule;
